import { useMemo } from 'react';

import { Skeleton, Stack, tokens, Typography } from '@cast/design-system';
import { ChartMetric, ChartMetricsRow } from '@cast/features-cluster-ui';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { P95LatencyDistributionChart } from './_components/P95LatencyDistributionChart';
import {
  LatencyBucket,
  useReliabilityDashboardData,
} from './_hooks/useReliabilityDashboardData';

type LatencyLegendKey = 'under100ms' | 'between100And500ms' | 'over500ms';

const LEGEND_TO_COLOR: Record<
  LatencyLegendKey,
  LatencyBucket['colorCategory']
> = {
  under100ms: 'green',
  between100And500ms: 'yellow',
  over500ms: 'red',
};

const LATENCY_LEGEND_ITEMS = [
  {
    key: 'under100ms' as const,
    label: 'Workloads under 100ms',
    color: tokens.theme.data.green500,
    testId: 'p95-latency-under-100ms',
  },
  {
    key: 'between100And500ms' as const,
    label: 'Workloads 100–500ms',
    color: tokens.theme.data.yellow500,
    testId: 'p95-latency-100-500ms',
  },
  {
    key: 'over500ms' as const,
    label: 'Workloads over 500ms',
    color: tokens.theme.data.red500,
    testId: 'p95-latency-over-500ms',
  },
];

const P95LatencyMetricsSkeleton = () => (
  <ChartMetricsRow alwaysRow alignItems="center" gap={24}>
    {LATENCY_LEGEND_ITEMS.map((item) => (
      <ChartMetric
        key={item.key}
        testId={`${item.testId}-skeleton`}
        color={item.color}
        label={item.label}
        value={<Skeleton variant="text" width={40} />}
        unit="%"
      />
    ))}
  </ChartMetricsRow>
);

type ReliabilityDashboardData = ReturnType<typeof useReliabilityDashboardData>;

export const P95LatencyDistributionCard = () => {
  const {
    latencyDistribution,
    latencyMetrics,
    isLoading,
    isFetching = isLoading,
    error,
    showNotInstalled,
    refetch,
  } = useReliabilityDashboardData() as ReliabilityDashboardData & {
    isFetching?: boolean;
  };
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<LatencyLegendKey>();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const visibleDistribution = useMemo(
    () =>
      latencyDistribution.map((bucket) => {
        const legendKey = (
          Object.entries(LEGEND_TO_COLOR) as [
            LatencyLegendKey,
            LatencyBucket['colorCategory']
          ][]
        ).find(([, color]) => color === bucket.colorCategory)?.[0];

        if (legendKey && isMetricDisabled(legendKey)) {
          return { ...bucket, count: 0 };
        }
        return bucket;
      }),
    [latencyDistribution, isMetricDisabled]
  );

  return (
    <WidgetLayout
      title="Workload P95 latency distribution"
      testId="p95-latency-distribution-card"
      headerDivider={false}
    >
      {hasError && !isFetching ? (
        <FailedToLoad
          title="Failed to load P95 latency distribution"
          refresh={refetch}
        />
      ) : (
        <Stack gap={16} height="100%">
          {isCardLoading ? (
            <P95LatencyMetricsSkeleton />
          ) : (
            <ChartMetricsRow alwaysRow alignItems="center" gap={24}>
              {LATENCY_LEGEND_ITEMS.map((item) => {
                const percent =
                  item.key === 'under100ms'
                    ? latencyMetrics.under100msPercent
                    : item.key === 'between100And500ms'
                    ? latencyMetrics.between100And500msPercent
                    : latencyMetrics.over500msPercent;

                return (
                  <ChartMetric
                    key={item.key}
                    testId={item.testId}
                    color={item.color}
                    label={item.label}
                    value={`${percent}`}
                    unit="%"
                    disabled={isMetricDisabled(item.key)}
                    onClick={() => toggleMetric(item.key)}
                  />
                );
              })}
            </ChartMetricsRow>
          )}
          {showNotInstalled && !isCardLoading ? (
            <Stack alignItems="center" justifyContent="center" flex={1} gap={8}>
              <Typography variant="P14R" color="textSecondary">
                Reliability metrics not installed
              </Typography>
            </Stack>
          ) : (
            <Stack flex={1} minHeight={200}>
              <P95LatencyDistributionChart
                data={visibleDistribution}
                isLoading={isCardLoading}
              />
            </Stack>
          )}
        </Stack>
      )}
    </WidgetLayout>
  );
};
